import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export interface ULThemeSpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  speed?: string;
  starFadeSpeed?: string;
  label?: string;
  starColor?: string;
  dotColor?: string;
}

const sizeClasses: Record<string, string> = {
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-12',
  lg: 'size-16',
  xl: 'size-20',
};

const ULThemeSpinner = ({
  size = 'md',
  speed = '0.6s',
  starFadeSpeed = '1.2s',
  label = 'Loading...',
  starColor = 'var(--primary)',
  dotColor = 'var(--accent)',
  className,
  ...rest
}: ULThemeSpinnerProps) => {
  return (
    <div
      className={cn('relative inline-block', sizeClasses[size], className)}
      data-testid="ul-theme-spinner"
      {...rest}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 85 93"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>{label}</title>
        {/* Star shape — fades in/out */}
        <g transform="translate(5, 0)">
          <path
            fill={starColor}
            d="M78.0501 71.1499C76.7223 72.5974 75.0312 73.6629 73.1524 74.2359C71.2736 74.8088 69.2757 74.8682 67.3662 74.4079C65.4567 73.9476 63.7052 72.9845 62.2938 71.6184C60.8824 70.2524 59.8625 68.5334 59.3401 66.6399C58.6475 64.3821 57.1254 62.4705 55.0801 61.2899L46.4001 55.9999V66.1699C46.4104 68.6223 47.3214 70.9853 48.9601 72.8099C50.2325 74.2509 51.1073 75.9987 51.4983 77.8809C51.8893 79.7631 51.783 81.7147 51.1898 83.5433C50.5966 85.3718 49.537 87.0142 48.1156 88.3084C46.6941 89.6027 44.9599 90.504 43.0839 90.9237C41.2079 91.3433 39.2548 91.2667 37.4174 90.7015C35.58 90.1362 33.9217 89.1018 32.606 87.7003C31.2902 86.2987 30.3625 84.5784 29.9143 82.709C29.4661 80.8396 29.5129 78.8857 30.0501 77.0399C30.5672 75.3977 31.4221 73.8819 32.5601 72.5899C35.3101 69.3199 35.2801 65.4099 35.2801 61.9199C35.2801 52.8699 30.5701 44.9199 23.0601 41.0499C20.0601 39.4999 17.4501 38.4299 13.2701 38.8299C11.7936 39.028 10.295 38.9909 8.83008 38.7199C7.69454 38.4454 6.60835 37.9967 5.61007 37.3899C4.09683 36.4456 2.83596 35.1474 1.93609 33.6073C1.03623 32.0671 0.524389 30.3313 0.4447 28.5494C0.365011 26.7674 0.719866 24.9928 1.47867 23.3785C2.23747 21.7642 3.37744 20.3587 4.80035 19.2831C6.22326 18.2074 7.88642 17.4939 9.64645 17.2041C11.4065 16.9143 13.2106 17.0568 14.9033 17.6194C16.5959 18.182 18.1264 19.1479 19.3627 20.4336C20.5991 21.7194 21.5042 23.2865 22.0001 24.9999C22.6958 27.2535 24.2174 29.1609 26.2601 30.3399L34.9301 35.6299V25.4099C34.9226 22.9588 34.0151 20.5959 32.3801 18.7699C30.6044 16.7649 29.626 14.1781 29.6301 11.4999C29.643 9.71529 30.09 7.96063 30.9324 6.38731C31.7748 4.81399 32.9873 3.46925 34.4654 2.46913C35.9435 1.46901 37.6427 0.843561 39.4165 0.646712C41.1903 0.449863 42.9853 0.687546 44.6467 1.33925C46.3081 1.99095 47.786 3.0371 48.9528 4.38743C50.1197 5.73777 50.9405 7.35173 51.3444 9.09007C51.7483 10.8284 51.7232 12.6389 51.2712 14.3654C50.8192 16.0919 49.954 17.6824 48.7501 18.9999C47.1913 20.7706 46.3528 23.0613 46.4001 25.4199V35.5999L55.0801 30.3099C57.1228 29.1309 58.6444 27.2235 59.3401 24.9699C59.9269 22.9284 61.0923 21.1004 62.6954 19.7068C64.2985 18.3132 66.2709 17.4136 68.3742 17.1167C70.4776 16.8197 72.6219 17.1381 74.5482 18.0333C76.4745 18.9286 78.1004 20.3625 79.2295 22.1618C80.3585 23.961 80.9425 26.0487 80.9108 28.1727C80.8792 30.2966 80.2332 32.366 79.0509 34.1308C77.8687 35.8955 76.2008 37.2803 74.2486 38.1177C72.2965 38.9552 70.1436 39.2095 68.0501 38.8499C65.7674 38.3795 63.3916 38.7601 61.3701 39.9199L51.7001 45.8099L61.3701 51.6899C63.3906 52.8522 65.7662 53.2363 68.0501 52.7699C70.3359 52.3413 72.6988 52.6483 74.7993 53.6467C76.8998 54.6451 78.6298 56.2835 79.7408 58.3267C80.8518 60.3698 81.2867 62.7125 80.9829 65.0183C80.6791 67.3241 79.6523 69.4742 78.0501 71.1599"
            style={{ animation: `star-fade ${starFadeSpeed} ease-in-out infinite` }}
          />
        </g>
        {/* Dot — orbits the star */}
        <g transform="translate(5, 0)">
          <circle
            cx="0"
            cy="0"
            r="11"
            fill={dotColor}
            style={{
              animation: `dot-orbit ${speed} linear infinite`,
              transformOrigin: '0 0',
            }}
          />
        </g>
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  );
};

export default ULThemeSpinner;
